"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";

interface Step {
  label: string;
  index: number;
}

interface StepIndicatorProps {
  steps: Step[];
  currentStep: number;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({ steps, currentStep }) => {
  const uniqueLabels = useMemo(() => Array.from(new Set(steps.map((step) => step.label))), [steps]);
  const circleRefs = uniqueLabels.map(() => useRef<HTMLDivElement>(null));

  const [lineDimensions, setLineDimensions] = useState({
    progressWidth: 0,
    totalWidth: 0,
    left: 0,
  });

  const calculateDimensions = () => {
    if (circleRefs.some((ref) => !ref.current)) return;

    const circlePositions = circleRefs.map((ref) => {
      const rect = ref.current!.getBoundingClientRect();
      return { x: rect.left + rect.width / 2 };
    });

    const firstRect = circleRefs[0].current!.getBoundingClientRect();
    const containerRect = circleRefs[0].current!.parentElement?.parentElement?.getBoundingClientRect();
    const left = containerRect ? firstRect.left - containerRect.left : 0;
    const totalWidth = circlePositions[circlePositions.length - 1].x - circlePositions[0].x;

    const currentSectionIndex = uniqueLabels.findIndex((label) => {
      const sectionSteps = steps.filter((step) => step.label === label);
      const firstIndex = steps.indexOf(sectionSteps[0]);
      const lastIndex = steps.indexOf(sectionSteps[sectionSteps.length - 1]);
      return currentStep >= firstIndex && currentStep <= lastIndex;
    });

    if (currentSectionIndex === -1) {
      setLineDimensions((prev) => ({
        progressWidth: 0,
        totalWidth,
        left,
      }));
      return;
    }

    let progressWidth = 0;
    if (currentSectionIndex > 0) {
      progressWidth = circlePositions[currentSectionIndex].x - circlePositions[0].x;
    }

    if (currentSectionIndex < circlePositions.length - 1) {
      const currentLabel = uniqueLabels[currentSectionIndex];
      const sectionSteps = steps.filter((step) => step.label === currentLabel);
      const currentStepInSection = currentStep - steps.indexOf(sectionSteps[0]);

      const sectionWidth = circlePositions[currentSectionIndex + 1].x - circlePositions[currentSectionIndex].x;
      const partialProgress = (currentStepInSection / sectionSteps.length) * sectionWidth;
      progressWidth += partialProgress;
    }

    setLineDimensions((prev) => ({
      progressWidth,
      totalWidth,
      left,
    }));
  };

  useEffect(() => {
    calculateDimensions();

    const handleResize = () => {
      requestAnimationFrame(calculateDimensions);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [currentStep, steps, uniqueLabels]);

  return (
    <div className="no-scrollbar relative mt-8 w-full overflow-x-auto bg-white py-6">
      <div className="md:min-w-[600px] max-w-[400px] md:max-w-full">
        <div className="relative flex justify-between">
          {/* Background line */}
          <div
            className="absolute top-[3px] h-[2px] bg-gray-200"
            style={{
              left: lineDimensions.left,
              width: lineDimensions.totalWidth,
              zIndex: 1,
            }}
          />
          {/* Progress line */}
          <div
            className="absolute top-[3px] h-[2px] bg-red-500 transition-all duration-300"
            style={{
              left: lineDimensions.left,
              width: lineDimensions.progressWidth,
              zIndex: 2,
            }}
          />

          {uniqueLabels.map((label, index) => {
            const isActive = currentStep >= steps.findIndex((step) => step.label === label);

            return (
              <div
                key={`${label}-${index}`}
                className={`flex flex-col ${
                  index === 0 ? "items-center" : index === uniqueLabels.length - 1 ? "items-center" : "items-center"
                }`}
              >
                <div className="relative">
                  <div
                    ref={circleRefs[index]}
                    className={`relative h-[8px] w-[8px] rounded-full ${isActive ? "bg-red-500" : "bg-gray-300"} z-[3]`}
                  />
                  {/* Larger circle outline */}
                  <div
                    className={`absolute left-[-2px] top-[-2px] h-[12px] w-[12px] rounded-full border-4 ${isActive ? "border-red-500" : "border-gray-300"} z-[3]`}
                  />
                </div>
                <span
                  className={`inter mt-3 text-center text-sm font-medium ${isActive ? "text-red-500" : "text-gray-400"}`}
                >
                  {label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StepIndicator;
